import React from "react";
import { UpdateFunction } from "@hex-insights/app-modules";
import { ClassNameProps, DistributiveOmit, If, StyleProps } from "@hex-insights/core";
import { Form, FormEditButtonPair, FormState, SubmitButton, useInternalField } from "@hex-insights/forms";
import { StudentNoteDetailQuery, StudentNoteUpdateMutation } from "../../../Utilities";
import { StudentNoteFormState, StudentNoteFormValues } from "../../FormUtilities";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import { useDetailFormControl, useDetailFormSubmission, useReadOnlyForm } from "./hooks";

type StudentNoteResolutionFormValues = Pick<
	StudentNoteFormValues.Detail,
	"isResolved" | "resolutionDescription" | "resolvedAt"
>;

const resolutionFormFields = {
	isResolved: true,
	resolutionDescription: true,
	resolvedAt: true,
} as const;

export type ControlledDetailProps = Omit<
	DetailProps<StudentNoteUpdateMutation["updateStudentNote"]>,
	"formState" | "update"
> & {
	formStateArgs?: Partial<DistributiveOmit<StudentNoteFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

/**
 * Detail form of the StudentNote model using an internally managed form state.
 */
export function ControlledDetail({ formStateArgs, ...props }: ControlledDetailProps) {
	const { formState, update } = useDetailFormControl<keyof StudentNoteResolutionFormValues>({
		...formStateArgs,
		studentNote: props.studentNote,
		fields: resolutionFormFields,
	});

	return <Detail formState={formState} update={update} {...props} />;
}

export type DetailProps<T> = {
	studentNote: Pick<
		StudentNoteDetailQuery["studentNote"],
		"id" | "isResolved" | "resolutionDescription" | "resolvedAt"
	>;
	formState: FormState<StudentNoteResolutionFormValues>;
	update: UpdateFunction<StudentNoteResolutionFormValues, T>;
	onSuccess: (formState: FormState<StudentNoteResolutionFormValues>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<StudentNoteResolutionFormValues> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Detail form of the StudentNote model using the given form state.
 */
export function Detail<T>({
	formState,
	studentNote,
	update,
	onSuccess,
	formNameSuffix,
	noChangesDialog = false,
	...props
}: DetailProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName = `studentNote.detail.resolution.${studentNote.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	useInternalField(formState, "isResolved", true);
	useInternalField(formState, "resolvedAt", new Date().toISOString());

	return (
		<Form
			name={formName}
			formState={formState}
			onSubmit={formState.onSubmitWrapper(onSubmit)}
			noChangesDialog={noChangesDialog}
			{...props}
		>
			<Form.Column style={{ width: "100%" }}>
				<FormEditButtonPair formState={formState} />

				<DetailFields formState={formState} studentNote={studentNote} />

				<If condition={noChangesDialog}>
					<Form.ActionBar>
						<SubmitButton onClick={formState.onSubmitWrapper(onSubmit)} submissionStatus={formState.submissionStatus}>
							Mark as Resolved
						</SubmitButton>
					</Form.ActionBar>
				</If>
			</Form.Column>
		</Form>
	);
}

export type ControlledReadOnlyProps = Omit<ReadOnlyProps, "formState"> & {
	formStateArgs?: Partial<DistributiveOmit<StudentNoteFormState.UseReadOnlyFormStateArgs, "initialFormValues">>;
};

/**
 * Read-only detail form of the StudentNote model using an internally managed form state.
 */
export function ControlledReadOnly({ formStateArgs, ...props }: ControlledReadOnlyProps) {
	const { formState } = useReadOnlyForm<keyof StudentNoteResolutionFormValues>({
		...formStateArgs,
		studentNote: props.studentNote,
		fields: resolutionFormFields,
	});

	return <ReadOnly formState={formState} {...props} />;
}

export type ReadOnlyProps = {
	studentNote: Pick<
		StudentNoteDetailQuery["studentNote"],
		"id" | "isResolved" | "resolutionDescription" | "resolvedAt"
	>;
	formState: FormState<StudentNoteResolutionFormValues>;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Read-only detail form of the StudentNote model using the given form state.
 */
export function ReadOnly({ formState, studentNote, className, style }: ReadOnlyProps) {
	return (
		<div className={className} style={style}>
			<Form.Column>
				<DetailFields formState={formState} studentNote={studentNote} />
			</Form.Column>
		</div>
	);
}

type DetailFieldsProps = {
	studentNote: Pick<
		StudentNoteDetailQuery["studentNote"],
		"id" | "isResolved" | "resolutionDescription" | "resolvedAt"
	>;
	formState: FormState<StudentNoteResolutionFormValues>;
};

function DetailFields({ formState }: DetailFieldsProps) {
	return (
		<React.Fragment>
			<Field.ResolutionDescription formState={formState} style={{ width: "100%" }} />
		</React.Fragment>
	);
}
